/** Constants for screen tree node identification and validation. */

/**
 * Represents a node in the screen structure tree.
 */
export interface ScreenNode {
  type?: string;
  key?: string;
  id?: string;
  data?: {
    id?: string;
    properties?: ScreenNodeProperties;
    [key: string]: unknown;
  };
  children?: ScreenNode[];
  parent?: ScreenNode;
  title?: string;
  [key: string]: unknown;
}

/**
 * Represents the properties of a screen node.
 */
export interface ScreenNodeProperties {
  dataKey?: string;
  elementId?: string;
  label?: string;
  data?: string;
  title?: string;
  name?: string;
  pageTitle?: string;
  styleClass?: string[];
  [key: string]: unknown;
}

/**
 * Represents the generated identifier, elementId and style class names.
 */
export interface GeneratedNodeNames {
  dataKey: string;
  elementId: string;
  styleClass: string;
}

/**
 * Represents the result of a title validation operation.
 */
export interface ValidationResult {
  isValid: boolean;
  isDuplicate: boolean;
  messageKey?: string;
}

export const BUTTON_TYPE = 'button';
export const CUSTOM_BUTTON_TYPE = 'custombutton';
export const TAB_TYPE = 'tab';
export const TABVIEW_TYPE = 'tabview';
export const FORM_FIELD_TYPE = 'formfield';

/** Root action bar types defining validation boundary. */
export const ROOT_ACTION_BAR_TYPES = ['leftactionbar', 'rightactionbar', 'workflowactionbar', 'actionbar'];

/** Page types for parent page lookup. */
export const PAGE_TYPES = ['page', 'detailpage', 'listpage'];

/** Node types sharing page-level identifier context. */
export const PAGE_LEVEL_IDENTIFIER_TYPES = [
  'formfield',
  'captionitem',
  'gridcolumn',
  'searchfield',
  'filterfield',
  'tab',
  'formsection',
  'accordionformsection',
  'htmlcontainer',
  'inlinetext',
  'tabview'
];

/** Node types that directly map to container prefixes without context lookup by parent. */
export const DIRECT_PREFIX_TYPES = ['standarddatagrid', 'childlist', 'queryview', 'customcomponent', 'buttongroup'];

/** Container types that trigger prefix lookup. */
export const PREFIXED_PAGE_CONTAINER_TYPES = [
  'captionbar',
  'tablesearch',
  'quickfilter',
  'form',
  'grid',
  'htmlcontainer',
  'inlinetext',
  'formsection',
  'tabview'
];

/** Container prefixes for identifier generation. */
export const CONTAINER_PREFIXES: Record<string, string> = {
  // Page-level containers
  captionbar: 'cb',
  tablesearch: 'ts',
  quickfilter: 'qf',
  form: 'fm',
  grid: 'tbl',
  htmlcontainer: 'hc',
  inlinetext: 'in-txt',
  formsection: 'fs',
  tabview: 'tv',
  custombutton: 'cst-btn',
  standarddatagrid: 'sdg',
  childlist: 'cdg',
  queryview: 'qv',
  customcomponent: 'cc',
  buttongroup: 'bg',

  // Action bar containers (for buttons)
  leftactionbar: 'l_ab',
  rightactionbar: 'r_ab',
  workflowactionbar: 'w_ab',
  actionbar: 'ab'
};
